import type {
  CollectionActions,
  CollectionStateSlice,
  Reference,
} from '@freelancer/datastore/core';
import {
  mergeDocuments,
  mergeWebsocketDocuments,
  removeDocumentById,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import type { ServiceOfferingAttachmentsCollection } from '../service-offering-attachments';
import { transformServiceOffering } from './service-offerings.transformers';
import type { ServiceOfferingsCollection } from './service-offerings.types';

export function serviceOfferingsReducer(
  state: CollectionStateSlice<ServiceOfferingsCollection> = {},
  action:
    | CollectionActions<ServiceOfferingsCollection>
    | CollectionActions<ServiceOfferingAttachmentsCollection>,
): CollectionStateSlice<ServiceOfferingsCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'serviceOfferings') {
        const { result, ref, order } = action.payload;

        return mergeDocuments<ServiceOfferingsCollection>(
          state,
          transformIntoDocuments(
            result.service_offerings,
            transformServiceOffering,
          ),
          order,
          ref,
        );
      }

      if (action.payload.type === 'serviceOfferingAttachments') {
        const { result } = action.payload;

        const ref: Reference<ServiceOfferingsCollection> = {
          path: {
            collection: 'serviceOfferings',
            authUid: action.payload.ref.path.authUid,
          },
        };

        return updateWebsocketDocuments<ServiceOfferingsCollection>(
          state,
          [result.id],
          serviceOffering => ({
            ...serviceOffering,
            attachments: result.service_offering_attachments,
          }),
          ref,
        );
      }

      return state;
    }

    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'serviceOfferings') {
        const { ref, result } = action.payload;

        return mergeWebsocketDocuments<ServiceOfferingsCollection>(
          state,
          transformIntoDocuments(
            [result.service_offering],
            transformServiceOffering,
          ),
          ref,
        );
      }

      if (action.payload.type === 'serviceOfferingAttachments') {
        const { document, result } = action.payload;
        const ref: Reference<ServiceOfferingsCollection> = {
          path: {
            collection: 'serviceOfferings',
            authUid: action.payload.ref.path.authUid,
          },
        };

        return updateWebsocketDocuments<ServiceOfferingsCollection>(
          state,
          [document.serviceOfferingId],
          serviceOffering => ({
            ...serviceOffering,
            attachments: [
              ...(serviceOffering.attachments ?? []),
              ...result.service_offering_attachments,
            ],
          }),
          ref,
        );
      }

      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'serviceOfferings') {
        const { originalDocument, ref, result } = action.payload;

        if (isServiceOfferingsAttachmentUpdateResult(result)) {
          return state;
        }

        return mergeWebsocketDocuments<ServiceOfferingsCollection>(
          state,
          transformIntoDocuments(
            // The API endpoint doesn't include the attachments list in the response. And since the attachments
            // list cannot be modified via the update endpoint anyway (there's a separate endpoint for that),
            // we just use the attachments list from the original document.
            [
              {
                ...result.service_offering,
                attachments: originalDocument.attachments,
              },
            ],
            transformServiceOffering,
          ),
          ref,
        );
      }

      return state;
    }

    case 'API_DELETE_SUCCESS': {
      if (action.payload.type === 'serviceOfferings') {
        const { originalDocument, ref } = action.payload;
        return removeDocumentById<ServiceOfferingsCollection>(
          ref,
          state,
          originalDocument.id,
        );
      }

      if (action.payload.type === 'serviceOfferingAttachments') {
        const { originalDocument } = action.payload;
        const ref: Reference<ServiceOfferingsCollection> = {
          path: {
            collection: 'serviceOfferings',
            authUid: action.payload.ref.path.authUid,
          },
        };

        return updateWebsocketDocuments<ServiceOfferingsCollection>(
          state,
          [originalDocument.serviceOfferingId],
          serviceOffering => ({
            ...serviceOffering,
            attachments: serviceOffering.attachments
              ? serviceOffering.attachments.filter(
                  attachment => attachment.id !== originalDocument.id,
                )
              : [],
          }),
          ref,
        );
      }

      return state;
    }

    default: {
      return state;
    }
  }
}

function isServiceOfferingsAttachmentUpdateResult(
  result:
    | ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType']
    | ServiceOfferingsCollection['Backend']['Update']['ReturnType'],
): result is ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType'] {
  return (
    (
      result as ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType']
    ).service_offering_attachments !== undefined
  );
}
